import React, { useEffect, useState } from "react";
import AppContext from "./AppContext";
import { LocationCluster, Report, User } from "../utils/Interfaces";
import { getAuth, onAuthStateChanged, UserInfo } from "firebase/auth";
import { APIService } from "../network/APIService";
import AuthenticationLoading from "../components/AuthenticationLoading";
import { DEFAULT_USER } from "../utils/Constants";

// acts as a wrapper for the whole app
interface AppContextProviderProps {
  children: React.ReactNode;
}

const AppContextProvider: React.FC<AppContextProviderProps> = ({ children }) => {
  const [reports, setReports] = useState<Report[]>([]);
  const [firebaseUserLogInInfo, setFirebaseUserLogInInfo] = useState<UserInfo | null>(null);
  const [currentUser, setCurrentUser] = useState<User>(DEFAULT_USER);
  const [authenticatingUser, setAuthenticatingUser] = useState(true);
  const [jwt, setJWT] = useState<string>("");
  const [userToEdit, setUserToEdit] = useState<User>(DEFAULT_USER);
  const [selectedLocationCluster, setSelectedLocationCluster] = useState<LocationCluster | null>(null);

  useEffect(() => {
    // basic setup of api services
    APIService.initFirebase();
  }, []);

  useEffect(() => {
    // this callback is fired every time a user signs in/out of the app
    // a null value for userinfo means that the user does not have an active session with the app
    // a non-null value for userinfo means that the user has already logged in and has an active session with the app
    const unsub = onAuthStateChanged(
      getAuth(),
      userInfo => {
        // console.log("auth state changed detected. user info is: ", userInfo);
        setFirebaseUserLogInInfo(userInfo);
        setAuthenticatingUser(false);
      },
      // error call back
      () => {
        setAuthenticatingUser(false);
      }
    );

    return () => {
      unsub();
    };
  }, []);

  const value = {
    reports: reports,
    setReports: setReports,
    firebaseUserLogInInfo: firebaseUserLogInInfo,
    setFirebaseUserLogInInfo: setFirebaseUserLogInInfo,
    currentUser: currentUser,
    setCurrentUser: setCurrentUser,
    jwt: jwt,
    setJWT: setJWT,
    userToEdit: userToEdit,
    setUserToEdit: setUserToEdit,
    selectedLocationCluster: selectedLocationCluster,
    setSelectedLocationCluster: setSelectedLocationCluster,
  };

  if (authenticatingUser) {
    return <AuthenticationLoading />;
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppContextProvider;
